import { useEffect, useMemo, useState } from "react";
import { AdminApi } from "api/admin.api";
import api from "api/axiosInstance";
import { DynamicFormProps } from "types/admin";
import { columnTranslations } from "../config/columnTranslations";
import styles from "../AdminPanel.module.scss";

type ClinicInfoRow = {
  id: number;
  name: string;
  detail: string;
};

type CategoryRow = {
  id: number;
  title: string;
};

type GalleryRow = {
  id: number;
  title?: string;
  image_path?: string;
  category_id?: number;
};

type EmployeeRow = {
  id: number;
  surname: string;
  name: string;
  patronymic?: string;
  position_at_work?: string;
  description?: string;
  avatar_path_id?: number;
};

const SERVICES_PAGE_NOTE_KEY = "services.page.note";
const SERVICES_PAGE_NOTE_DEFAULT = "Список услуг подгружается автоматически.";

const HOME_HERO_EMPLOYEE_ID_KEY = "home.hero.employeeId";
const HOME_HERO_AVATAR_ID_KEY = "home.hero.avatarId";

function extractErr(e: any) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  const msg =
    typeof data === "string"
      ? data
      : data?.message
      ? Array.isArray(data.message)
        ? data.message.join(", ")
        : String(data.message)
      : data?.error
      ? String(data.error)
      : e?.message;

  return status ? `${msg} (HTTP ${status})` : msg || "Неизвестная ошибка";
}

function isCertificateCategory(title?: string) {
  const t = (title || "").toLowerCase();
  return t.includes("сертифик");
}

function safeImgSrc(path?: string) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return path;
  return `/${path}`;
}

function fullName(e?: EmployeeRow | null) {
  if (!e) return "—";
  return [e.surname, e.name, e.patronymic].filter(Boolean).join(" ").trim();
}

export const DynamicForm = ({
  table,
  schema,
  onSuccess,
  initialData,
}: DynamicFormProps) => {
  const [form, setForm] = useState<Record<string, any>>({});
  const [rawJsonb, setRawJsonb] = useState<Record<string, any>>({});
  const [refs, setRefs] = useState<Record<string, any[]>>({});

  const [galleryOptions, setGalleryOptions] = useState<GalleryRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [imageCategoryFilter, setImageCategoryFilter] = useState<number | null>(null);

  const [employeeRows, setEmployeeRows] = useState<EmployeeRow[]>([]);
  const [homeHeroEmployeeId, setHomeHeroEmployeeId] = useState<string>("");
  const [homeHeroEmployeeRow, setHomeHeroEmployeeRow] = useState<ClinicInfoRow | null>(null);
  const [homeHeroAvatarId, setHomeHeroAvatarId] = useState<string>("")
  const [homeHeroAvatarRow, setHomeHeroAvatarRow] = useState<ClinicInfoRow | null>(null)

  const [homeHeroLoading, setHomeHeroLoading] = useState(false);
  const [homeHeroError, setHomeHeroError] = useState<string | null>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const foreignMap: Record<string, string> = {
    image_path_id: "gallery",
    avatar_path_id: "gallery",
    preview_image_id: "gallery",
    service_id: "service",
    services_id: "service",
    category_id: "category",
    video_id: "videos",
  };

  const galleryJsonbColumns = ["sertificate_path_id", "work_path_id"];

  async function loadRefs(schema: any[]) {
    const result: Record<string, any[]> = {};

    const needGalleryRefs =
      schema.some((col) => foreignMap[col.column_name] === "gallery") ||
      schema.some((col) => galleryJsonbColumns.includes(col.column_name));

    let loadedCategories: CategoryRow[] = [];
    let sortedGallery: GalleryRow[] = [];

    if (needGalleryRefs) {
      const [galleryRes, categoryRes] = await Promise.all([
        AdminApi.getData("gallery"),
        AdminApi.getData("category"),
      ]);

      loadedCategories = (Array.isArray(categoryRes.data) ? categoryRes.data : []) as CategoryRow[];
      const galleryRows = (Array.isArray(galleryRes.data) ? galleryRes.data : []) as GalleryRow[];

      const categoryMap = loadedCategories.reduce<Record<number, string>>((acc, item) => {
        acc[item.id] = item.title;
        return acc;
      }, {});

      sortedGallery = [...galleryRows].sort((a, b) => {
        const catA = categoryMap[a.category_id ?? 0] ?? "Без категории";
        const catB = categoryMap[b.category_id ?? 0] ?? "Без категории";

        const byCategory = catA.localeCompare(catB, "ru");
        if (byCategory !== 0) return byCategory;

        const titleA = (a.title ?? "").trim();
        const titleB = (b.title ?? "").trim();
        const byTitle = titleA.localeCompare(titleB, "ru");
        if (byTitle !== 0) return byTitle;

        return (a.id ?? 0) - (b.id ?? 0);
      });
    }

    for (const col of schema) {
      const refTable = foreignMap[col.column_name];
      if (!refTable) continue;

      if (refTable === "gallery") {
        result[col.column_name] = sortedGallery;
      } else {
        const response = await AdminApi.getData(refTable);
        result[col.column_name] = Array.isArray(response.data) ? response.data : [];
      }
    }

    return { refs: result, categories: loadedCategories, sortedGallery };
  }

  const categoryTitleMap = useMemo(() => {
    return categories.reduce<Record<number, string>>((acc, item) => {
      acc[item.id] = item.title;
      return acc;
    }, {});
  }, [categories]);

  const getDisplayValue = (colName: string, opt: any) => {
    if (colName === "video_id") return opt.originalName;

    if (
      colName === "avatar_path_id" ||
      colName === "image_path_id" ||
      colName === "preview_image_id" ||
      colName === "sertificate_path_id"
    ) {
      const catTitle =
        typeof opt?.category_id === "number"
          ? categoryTitleMap[opt.category_id] || "Без категории"
          : "Без категории";

      const title = opt?.title?.trim?.() || `Изображение #${opt.id}`;
      return `[${catTitle}] ${title} (#${opt.id})`;
    }

    if (opt.title) return opt.title;
    if (opt.name) return opt.name;
    if (opt.originalName) return opt.originalName;

    return `#${opt.id}`;
  };

  const fileColumns = ["image_path"];
  const textAreaColumns = ["description", "how_treatment", "includes_in_price"];

  const hiddenColumnBase = ["id", "created_at", "updated_at", "published_at"];

  const isEdit = Boolean(initialData?.id);
  const isGallery = table === "gallery";
  const isServiceTable = table === "service";
  const isEmployeeTable = table === "employee";
  const isBlogPostTable = table === "blog_post";

  const hiddenColumns = useMemo(() => {
    const extra: string[] = [];

    if (table === "employee") {
      extra.push("work_path_id");
    }

    if (table === "blog_post") {
      extra.push("is_vertical_video");
    }

    return [...hiddenColumnBase, ...extra];
  }, [table]);

  const [servicesNote, setServicesNote] = useState(SERVICES_PAGE_NOTE_DEFAULT);
  const [servicesNoteRow, setServicesNoteRow] = useState<ClinicInfoRow | null>(null);
  const [servicesNoteLoading, setServicesNoteLoading] = useState(false);
  const [servicesNoteError, setServicesNoteError] = useState<string | null>(null);

  const loadServicesNote = async () => {
    if (!isServiceTable) return;

    setServicesNoteLoading(true);
    setServicesNoteError(null);

    try {
      const res = await api.get<ClinicInfoRow[]>("/clinic-info");
      const rows = Array.isArray(res.data) ? res.data : [];
      const row = rows.find((r) => r.name === SERVICES_PAGE_NOTE_KEY) ?? null;

      setServicesNoteRow(row);
      setServicesNote((row?.detail || "").trim() || SERVICES_PAGE_NOTE_DEFAULT);
    } catch (e: any) {
      setServicesNoteError(`Не удалось загрузить заметку: ${extractErr(e)}`);
      setServicesNoteRow(null);
      setServicesNote(SERVICES_PAGE_NOTE_DEFAULT);
    } finally {
      setServicesNoteLoading(false);
    }
  };

  const saveServicesNote = async () => {
    setServicesNoteLoading(true);
    setServicesNoteError(null);

    try {
      const clean = (servicesNote || "").trim() || SERVICES_PAGE_NOTE_DEFAULT;

      if (servicesNoteRow?.id) {
        await api.delete(`/clinic-info/${servicesNoteRow.id}`);
      }
      await api.post("/clinic-info", { name: SERVICES_PAGE_NOTE_KEY, detail: clean });

      await loadServicesNote();
    } catch (e: any) {
      setServicesNoteError(`Не удалось сохранить заметку: ${extractErr(e)}`);
    } finally {
      setServicesNoteLoading(false);
    }
  };

  const loadHomeHeroEmployee = async () => {
    if (!isEmployeeTable) return;

    setHomeHeroLoading(true);
    setHomeHeroError(null);

    try {
      const [ciRes, empRes] = await Promise.all([
        api.get<ClinicInfoRow[]>("/clinic-info"),
        api.get<EmployeeRow[]>("/employee"),
      ]);

      const rows = Array.isArray(ciRes.data) ? ciRes.data : [];
      const employees = Array.isArray(empRes.data) ? empRes.data : [];

      const employeeRow = rows.find((r) => r.name === HOME_HERO_EMPLOYEE_ID_KEY) ?? null;
       const avatarRow = rows.find((r) => r.name === HOME_HERO_AVATAR_ID_KEY) ?? null

      setEmployeeRows(employees);
     
      setHomeHeroEmployeeRow(employeeRow);
      setHomeHeroEmployeeId((employeeRow?.detail || "").trim());

      setHomeHeroAvatarRow(avatarRow);
      setHomeHeroAvatarId((avatarRow?.detail || "").trim());
    } catch (e: any) {
      setHomeHeroError(`Не удалось загрузить блок главной: ${extractErr(e)}`);
      setEmployeeRows([]);
      setHomeHeroEmployeeRow(null);
      setHomeHeroEmployeeId("");
      
      setHomeHeroAvatarRow(null)
      setHomeHeroAvatarId("")
    } finally {
      setHomeHeroLoading(false);
    }
  };

  const saveHomeHeroEmployee = async () => {
    setHomeHeroLoading(true);
    setHomeHeroError(null);

    try {
      const cleanEmployeeId = (homeHeroEmployeeId || "").trim();
      const cleanAvatarId = (homeHeroAvatarId || "").trim();

      if (homeHeroEmployeeRow?.id) {
        await api.delete(`/clinic-info/${homeHeroEmployeeRow.id}`);
      }

      if(homeHeroAvatarRow?.id){
	await api.delete(`/clinic-info/${homeHeroAvatarRow.id}`)
      }

      if (cleanEmployeeId) {
        await api.post("/clinic-info", {
          name: HOME_HERO_EMPLOYEE_ID_KEY,
          detail: cleanEmployeeId,
        });
      }

      if(cleanAvatarId){
        await api.post("/clinic-info",{
          name: HOME_HERO_AVATAR_ID_KEY,
	  detail: cleanAvatarId,
        })
      }

      await loadHomeHeroEmployee();
    } catch (e: any) {
      setHomeHeroError(`Не удалось сохранить сотрудника для главной: ${extractErr(e)}`);
    } finally {
      setHomeHeroLoading(false);
    }
  };

  useEffect(() => {
    if (isServiceTable) loadServicesNote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServiceTable]);

  useEffect(() => {
    if (isEmployeeTable) loadHomeHeroEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmployeeTable]);

  useEffect(() => {
    if (initialData) {
      const normalized: Record<string, any> = {};

      schema.forEach((col) => {
        const name = col.column_name;
        if (hiddenColumns.includes(name)) return;

        const val = initialData[name];

        if (foreignMap[name]) {
          normalized[name] = val ?? null;
          return;
        }

        if (col.data_type === "jsonb") {
          normalized[name] = Array.isArray(val) ? val : [];
        } else if (
          col.data_type === "integer" ||
          col.data_type === "int" ||
          col.data_type === "int4"
        ) {
          normalized[name] = typeof val === "number" ? val : null;
        } else if (col.data_type === "boolean") {
          normalized[name] = Boolean(val);
        } else if (
          (col.data_type === "date" || col.data_type === "timestamp") &&
          typeof val === "string"
        ) {
          normalized[name] = val.slice(0, 10);
        } else {
          normalized[name] = val ?? "";
        }
      });

      if (isBlogPostTable) {
        normalized.is_vertical_video = Boolean((initialData as any)?.is_vertical_video);
      }

      setForm(normalized);
    } else {
      const defaults: Record<string, any> = {};

      schema.forEach((col) => {
        const name = col.column_name;
        if (hiddenColumns.includes(name)) return;

        if (foreignMap[name]) {
          if (table === "gallery" && name === "category_id") {
            defaults[name] = 1;
          } else {
            defaults[name] = null;
          }
          return;
        }

        if (col.data_type === "jsonb") {
          defaults[name] = [];
        } else if (
          col.data_type === "integer" ||
          col.data_type === "int" ||
          col.data_type === "int4"
        ) {
          defaults[name] = null;
        } else if (col.data_type === "boolean") {
          defaults[name] = false;
        } else {
          defaults[name] = "";
        }
      });

      if (isBlogPostTable) {
        defaults.is_vertical_video = false;
      }

      setForm(defaults);
    }
  }, [initialData, schema, hiddenColumns, table, isBlogPostTable]);

  useEffect(() => {
    const raw: Record<string, any> = {};

    schema.forEach((col) => {
      if (col.data_type === "jsonb") {
        const arr = initialData?.[col.column_name];
        raw[col.column_name] = Array.isArray(arr) ? arr.join(",") : "";
      }
    });

    setRawJsonb(raw);
  }, [initialData, schema]);

  useEffect(() => {
    loadRefs(schema).then(({ refs, categories, sortedGallery }) => {
      setRefs(refs);
      setCategories(categories);
      setGalleryOptions(sortedGallery);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  const handleChange = (col: string, value: any) => {
    setForm((prev) => ({ ...prev, [col]: value }));
  };

  const filteredAvatarOptions = useMemo(() => {
    let items = galleryOptions;

    if (imageCategoryFilter) {
      items = items.filter((x) => x.category_id === imageCategoryFilter);
    }

    return items;
  }, [galleryOptions, imageCategoryFilter]);

  const certificateOnlyOptions = useMemo(() => {
    return galleryOptions.filter((x) => {
      const title = x.category_id ? categoryTitleMap[x.category_id] : "";
      return isCertificateCategory(title);
    });
  }, [galleryOptions, categoryTitleMap]);

  const filteredCertificateOptions = useMemo(() => {
    let items = certificateOnlyOptions;

    if (imageCategoryFilter) {
      items = items.filter((x) => x.category_id === imageCategoryFilter);
    }

    return items;
  }, [certificateOnlyOptions, imageCategoryFilter]);

  const selectedAvatarRow = useMemo(() => {
    const avatarId = form["avatar_path_id"];
    if (typeof avatarId !== "number") return null;
    return galleryOptions.find((x) => x.id === avatarId) ?? null;
  }, [form, galleryOptions]);

  const selectedCertificateRows = useMemo(() => {
    const ids = Array.isArray(form["sertificate_path_id"]) ? form["sertificate_path_id"] : [];
    if (!ids.length) return [];

    const idSet = new Set<number>(ids);
    return galleryOptions.filter((x) => idSet.has(x.id));
  }, [form, galleryOptions]);

  const selectedHomeHeroEmployee = useMemo(() => {
    const parsed = Number(homeHeroEmployeeId);
    if (!Number.isFinite(parsed)) return null;
    return employeeRows.find((x) => x.id === parsed) ?? null;
  }, [employeeRows, homeHeroEmployeeId]);

  const selectedHomeHeroAvatar = useMemo(() => {
    const overrideParsed = Number(homeHeroAvatarId)

    if(Number.isFinite(overrideParsed)) {
      const override = galleryOptions.find((x) => x.id === overrideParsed)
      if(override) return override
    }

    const avatarId = selectedHomeHeroEmployee?.avatar_path_id
    if(typeof avatarId !== "number") return null

    return galleryOptions.find((x) => x.id === avatarId) ?? null
  }, [homeHeroAvatarId, selectedHomeHeroEmployee, galleryOptions]);

  const sanitizePayload = (source: Record<string, any>) => {
    const payload: Record<string, any> = { ...source };

    delete payload.published_at;

    Object.keys(foreignMap).forEach((key) => {
      if (payload[key] === "") {
        payload[key] = null;
      }
    });

    if (
      table === "gallery" &&
      (payload.category_id === "" ||
        payload.category_id === null ||
        payload.category_id === undefined)
    ) {
      payload.category_id = 1;
    }

    if (table === "blog_post") {
      if (!payload.video_id) {
        payload.video_id = null;
        payload.is_vertical_video = false;
      } else {
        payload.is_vertical_video = Boolean(payload.is_vertical_video);
      }
    }

    return payload;
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    try {
      if (table === "gallery") {
        const title = String(form.title || "").trim();

        if (!title) {
          setSubmitError("Для галереи нужно указать название.");
          return;
        }

        if (!isEdit && !(form.image_path instanceof File)) {
          setSubmitError("Для галереи нужно выбрать изображение.");
          return;
        }
      }

      const hasFile = Object.values(form).some((v) => v instanceof File);

      if (hasFile) {
        const fd = new FormData();

        Object.entries(form).forEach(([key, value]) => {
          if (key === "published_at") return;

          if (
            table === "gallery" &&
            key === "category_id" &&
            (value === "" || value === null || value === undefined)
          ) {
            fd.append("category_id", "1");
            return;
          }

          if (foreignMap[key] && (value === "" || value === null || value === undefined)) {
            return;
          }

          if (table === "blog_post" && key === "is_vertical_video") {
            if (!form.video_id) {
              fd.append("is_vertical_video", "false");
            } else {
              fd.append("is_vertical_video", String(Boolean(value)));
            }
            return;
          }

          if (value instanceof File) {
            fd.append(key, value);
          } else if (Array.isArray(value)) {
            fd.append(key, JSON.stringify(value));
          } else if (typeof value === "number") {
            fd.append(key, String(value));
          } else if (typeof value === "boolean") {
            fd.append(key, String(value));
          } else if (value === null || value === undefined) {
            fd.append(key, "");
          } else {
            fd.append(key, String(value));
          }
        });

        fd.delete("id");
        fd.delete("created_at");
        fd.delete("updated_at");
        fd.delete("published_at");

        if (isEdit && initialData?.id) {
          if (isGallery) {
            fd.delete("image_path");
          }

          await AdminApi.updateFormData(table, initialData.id, fd);
          setForm({});
          onSuccess();
        } else {
          await AdminApi.createFormData(table, fd);
          setForm({});
          onSuccess();
        }

        return;
      }

      const payload = sanitizePayload(form);

      if (isEdit && initialData?.id) {
        const cleaned = Object.fromEntries(
          Object.entries(payload).filter(
            ([key]) => !["id", "created_at", "updated_at", "published_at"].includes(key)
          )
        );

        await AdminApi.update(table, initialData.id, cleaned);
        setForm({});
        onSuccess();
      } else {
        await AdminApi.create(table, payload);
        setForm({});
        onSuccess();
      }
    } catch (e: any) {
      setSubmitError(extractErr(e));
    }
  };

  const tableTitle = useMemo(() => columnTranslations[table] ?? table, [table]);

  const checkboxRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "fit-content",
    lineHeight: 1.2,
    marginTop: 2,
  };

  const checkboxInputStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    margin: 0,
    flex: "0 0 auto",
    transform: "translateY(0)",
  };

  return (
    <div className={styles.form_block}>
      {isServiceTable ? (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Услуги</h3>

          {servicesNoteError ? (
            <div style={{ color: "#c00", marginBottom: 10 }}>{servicesNoteError}</div>
          ) : null}

          <label>Заметка над списком услуг</label>
          <textarea
            value={servicesNote}
            onChange={(e) => setServicesNote(e.target.value)}
            rows={4}
            style={{ width: "60%" }}
            placeholder={SERVICES_PAGE_NOTE_DEFAULT}
          />

          <div style={{ display: "flex", gap: 10 }}>
            <button
              className={styles.btn_primary}
              type="button"
              onClick={saveServicesNote}
              disabled={servicesNoteLoading}
            >
              {servicesNoteLoading ? "Сохраняю..." : "Сохранить заметку"}
            </button>

            <button
              className={styles.btn}
              type="button"
              onClick={() => setServicesNote(SERVICES_PAGE_NOTE_DEFAULT)}
              disabled={servicesNoteLoading}
            >
              Сбросить к дефолту
            </button>
          </div>

          <div style={{ marginTop: 8, fontSize: 13, opacity: 0.75 }}>
            Поддерживаются переносы строк. На странице отображается как многострочный текст.
          </div>

          <hr style={{ margin: "16px 0" }} />
        </div>
      ) : null}

      {isEmployeeTable ? (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Сотрудник на главной странице</h3>

          {homeHeroError ? (
            <div style={{ color: "#c00", marginBottom: 10 }}>{homeHeroError}</div>
          ) : null}

          <label>Выбрать сотрудника</label>
          <select
            value={homeHeroEmployeeId}
            onChange={(e) => setHomeHeroEmployeeId(e.target.value)}
            style={{ width: "60%" }}
          >
            <option value="">(не выбрано — показывать текстовый блок)</option>
            {employeeRows.map((e) => (
              <option key={e.id} value={e.id}>
                {fullName(e)}
              </option>
            ))}
          </select>

	  <div style={{marginTop: 8, fontSize: 13, opacity: 0.75}}>
	    На главной будут использованы: ФИО, должность и описание сотрудника.
	  </div>

	  <label style={{marginTop: 12, display: "block"}}>Фото для главной</label>

	  <select
            value={homeHeroAvatarId}
            onChange={(e) => setHomeHeroAvatarId(e.target.value)}
            style={{width: "60%"}}
          >
	    <option value="">Использовать аватарку сотрудника</option>
	    {filteredAvatarOptions.map((opt) => (
	      <option key={opt.id} value={opt.id}>
		{getDisplayValue("avatar_path_id", opt)}
	      </option>
	    ))}

          </select>

          <div style={{ marginTop: 6, fontSize: 13, opacity: 0.75 }}>
           Если не выбранл - будет использована аватарка сотурдника.
          </div>

          {selectedHomeHeroEmployee ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Предпросмотр выбранного сотрудника
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "220px minmax(0, 1fr)",
                  gap: 16,
                  alignItems: "start",
                  maxWidth: 760,
                  border: "1px solid #e3e3e3",
                  borderRadius: 8,
                  padding: 12,
                  background: "#fff",
                }}
              >
                <div className={styles.avatarPreviewCard}>
                  <div className={styles.avatarPreviewMedia}>
                    {selectedHomeHeroAvatar?.image_path ? (
                      <img
                        src={safeImgSrc(selectedHomeHeroAvatar.image_path)}
                        alt={fullName(selectedHomeHeroEmployee)}
                        className={styles.avatarPreviewImg}
                      />
                    ) : (
                      <div className={styles.avatarPreviewFallback}>Нет фото</div>
                    )}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>
                    {fullName(selectedHomeHeroEmployee)}
                  </div>

                  {selectedHomeHeroEmployee.position_at_work ? (
                    <div style={{ marginBottom: 10, opacity: 0.78 }}>
                      {selectedHomeHeroEmployee.position_at_work}
                    </div>
                  ) : null}

                  {selectedHomeHeroEmployee.description ? (
                    <div style={{ whiteSpace: "pre-line", lineHeight: 1.5, opacity: 0.82 }}>
                      {selectedHomeHeroEmployee.description
                        .split(/\n|•|;/g)
                        .map((x) => x.trim())
                        .filter(Boolean)
                        .slice(0, 5)
                        .map((line) => `• ${line}`)
                        .join("\n")}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <button
              className={styles.btn_primary}
              type="button"
              onClick={saveHomeHeroEmployee}
              disabled={homeHeroLoading}
            >
              {homeHeroLoading ? "Сохраняю..." : "Сохранить"}
            </button>

            <button
              className={styles.btn}
              type="button"
              onClick={() => setHomeHeroEmployeeId("")}
              disabled={homeHeroLoading}
            >
              Сбросить
            </button>
          </div>

          <hr style={{ margin: "16px 0" }} />
        </div>
      ) : null}

      {submitError ? (
        <div style={{ color: "#c00", marginBottom: 12 }}>
          Ошибка сохранения: {submitError}
        </div>
      ) : null}

      <h3 style={{ marginTop: isServiceTable || isEmployeeTable ? 0 : undefined }}>
        {isEdit ? "Редактировать" : "Добавить запись"} в {tableTitle}
      </h3>

      {schema
        .filter((col) => !hiddenColumns.includes(col.column_name))
        .map((col) => {
          const name = col.column_name;
          const label = columnTranslations[name] ?? name;
          const value = form[name] ?? "";

          const isCertificateMultiSelect =
            isEmployeeTable && name === "sertificate_path_id";

          const isAvatarSelect = isEmployeeTable && name === "avatar_path_id";

          const isInlineBoolean = col.data_type === "boolean";

          return (
            <div key={name}>
              {isAvatarSelect ? (
                <div style={{ marginBottom: 16 }}>
                  <label>Категория для изображений</label>
                  <select
                    value={imageCategoryFilter ?? ""}
                    onChange={(e) =>
                      setImageCategoryFilter(e.target.value ? Number(e.target.value) : null)
                    }
                    style={{ width: "60%" }}
                  >
                    <option value="">Все категории</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.title}
                      </option>
                    ))}
                  </select>

                  <div style={{ marginTop: 6, fontSize: 13, opacity: 0.75 }}>
                    Этот фильтр применяется к аватарке и сертификатам. Для сертификатов даже при
                    выбранной категории показываются только категории с названием, содержащим
                    "сертифик".
                  </div>
                </div>
              ) : null}

              {!isInlineBoolean ? <label>{label}</label> : null}

              {col.data_type === "boolean" ? (
                <label style={checkboxRowStyle}>
                  <input
                    type="checkbox"
                    checked={form[name] ?? false}
                    onChange={(e) => handleChange(name, e.target.checked)}
                    style={checkboxInputStyle}
                  />
                  <span>{label}</span>
                </label>
              ) : fileColumns.includes(name) ? (
                <input
                  type="file"
                  onChange={(e) => handleChange(name, e.target.files?.[0] || null)}
                />
              ) : isCertificateMultiSelect ? (
                <div>
                  <select
                    multiple
                    value={(Array.isArray(form[name]) ? form[name] : []).map(String)}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions).map((opt) =>
                        Number(opt.value)
                      );
                      handleChange(name, selected);
                    }}
                    size={Math.min(12, Math.max(6, filteredCertificateOptions.length || 6))}
                    style={{ width: "60%", minHeight: 220 }}
                  >
                    {filteredCertificateOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {getDisplayValue(name, opt)}
                      </option>
                    ))}
                  </select>

                  <div style={{ marginTop: 6, fontSize: 13, opacity: 0.75 }}>
                    Изображения отсортированы по категориям. Можно выбирать несколько значений
                    через Ctrl / Cmd.
                  </div>

                  {selectedCertificateRows.length > 0 ? (
                    <div className={styles.employeeWorksPhotoGrid} style={{ marginTop: 12 }}>
                      {selectedCertificateRows.map((item) => {
                        const src = safeImgSrc(item.image_path);
                        const catTitle =
                          typeof item.category_id === "number"
                            ? categoryTitleMap[item.category_id] || "Без категории"
                            : "Без категории";

                        return (
                          <div key={item.id} className={styles.employeeWorksPhotoCard}>
                            {src ? (
                              <img
                                src={src}
                                alt={item.title || `Сертификат #${item.id}`}
                                className={styles.employeeWorksPhotoImg}
                              />
                            ) : (
                              <div className={styles.employeeWorksPhotoFallback}>Нет превью</div>
                            )}

                            <div className={styles.employeeWorksPhotoMeta}>
                              <div className={styles.employeeWorksPhotoTitle}>
                                {item.title || `Изображение #${item.id}`}
                              </div>
                              <div style={{ fontSize: 12, opacity: 0.7 }}>{catTitle}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ) : isAvatarSelect ? (
                <div>
                  <select
                    value={form[name] ?? ""}
                    onChange={(e) =>
                      handleChange(
                        name,
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                    style={{ width: "60%" }}
                  >
                    <option value="">Выберите значение</option>
                    {filteredAvatarOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {getDisplayValue(name, opt)}
                      </option>
                    ))}
                  </select>

                  {selectedAvatarRow ? (
                    <div className={styles.avatarPreviewWrap}>
                      <div className={styles.avatarPreviewCard}>
                        <div className={styles.avatarPreviewMedia}>
                          {safeImgSrc(selectedAvatarRow.image_path) ? (
                            <img
                              src={safeImgSrc(selectedAvatarRow.image_path)}
                              alt={selectedAvatarRow.title || "Аватарка"}
                              className={styles.avatarPreviewImg}
                            />
                          ) : (
                            <div className={styles.avatarPreviewFallback}>Нет превью</div>
                          )}
                        </div>

                        <div className={styles.employeeWorksPhotoMeta}>
                          <div className={styles.employeeWorksPhotoTitle}>
                            {selectedAvatarRow.title || `Изображение #${selectedAvatarRow.id}`}
                          </div>
                          <div style={{ fontSize: 12, opacity: 0.7 }}>
                            {typeof selectedAvatarRow.category_id === "number"
                              ? categoryTitleMap[selectedAvatarRow.category_id] || "Без категории"
                              : "Без категории"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : foreignMap[name] ? (
                <div>
                  <select
                    value={form[name] ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value;

                      if (raw === "") {
                        handleChange(name, null);

                        if (isBlogPostTable && name === "video_id") {
                          handleChange("is_vertical_video", false);
                        }

                        return;
                      }

                      const sample = refs[name]?.[0];

                      if (sample && typeof sample.id === "string") {
                        handleChange(name, raw);
                      } else {
                        handleChange(name, Number(raw));
                      }
                    }}
                  >
                    <option value="">Выберите значение</option>
                    {(refs[name] ?? []).map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {getDisplayValue(name, opt)}
                      </option>
                    ))}
                  </select>

                  {isBlogPostTable && name === "video_id" ? (
                    <div style={{ marginTop: 10 }}>
                      <label
                        style={{
                          ...checkboxRowStyle,
                          cursor: form.video_id ? "pointer" : "not-allowed",
                          opacity: form.video_id ? 1 : 0.65,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(form.is_vertical_video)}
                          disabled={!form.video_id}
                          onChange={(e) =>
                            handleChange("is_vertical_video", e.target.checked)
                          }
                          style={checkboxInputStyle}
                        />
                        <span>Вертикальное видео</span>
                      </label>

                      <div style={{ marginTop: 6, fontSize: 13, opacity: 0.75 }}>
                        Включайте только для роликов формата 9:16. На странице поста они будут
                        показываться по центру в вертикальном контейнере.
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : col.data_type === "integer" || col.data_type === "int" || col.data_type === "int4" ? (
                <input
                  type="number"
                  value={typeof form[name] === "number" ? form[name] : ""}
                  onChange={(e) =>
                    handleChange(name, e.target.value === "" ? null : Number(e.target.value))
                  }
                />
              ) : col.data_type === "date" || col.data_type === "timestamp" ? (
                <input
                  type="date"
                  value={value ? String(value).slice(0, 10) : ""}
                  onChange={(e) => handleChange(name, e.target.value)}
                />
              ) : col.data_type === "jsonb" ? (
                <textarea
                  value={rawJsonb[name] ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setRawJsonb((prev) => ({ ...prev, [name]: raw }));

                    if (raw.trim() === "") {
                      handleChange(name, []);
                      return;
                    }

                    if (/^\d+(,\d*)*,?$/.test(raw)) {
                      const numbers = raw
                        .split(",")
                        .filter((n) => n.trim() !== "")
                        .map((n) => Number(n));

                      handleChange(name, numbers);
                    }
                  }}
                  rows={4}
                />
              ) : textAreaColumns.includes(name) ? (
                <div>
                  <textarea
                    value={value}
                    onChange={(e) => handleChange(name, e.target.value)}
                    rows={6}
                    style={{ width: "60%" }}
                    placeholder="Каждый пункт с новой строки"
                  />

                  {(name === "how_treatment" || name === "includes_in_price") && (
                    <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "center" }}>
                      <div style={{ fontSize: 13, opacity: 0.75 }}>
                        1 пункт = 1 строка. Можно вставлять списком.
                      </div>

                      <button
                        className={styles.btn}
                        type="button"
                        onClick={() => {
                          if (name === "how_treatment") {
                            handleChange(
                              name,
                              [
                                "Избегайте горячей пищи первые сутки.",
                                "Чистите зубы аккуратно, обходя зону лечения.",
                                "Ограничьте спорт и банные процедуры на 2-3 дня.",
                                "Принимайте назначенные препараты по схеме.",
                              ].join("\n")
                            );
                          } else {
                            handleChange(
                              name,
                              [
                                "Не употребляйте алкоголь и не курите первые сутки.",
                                "Не трогать область лечения руками или языком",
                                "Не принимайте препараты разжижающие кровь, без врача.",
                                "Не прогревайте место лечения и не используйте горячие полоскания",
                              ].join("\n")
                            );
                          }
                        }}
                      >
                        Сгенерировать 4 пункта
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleChange(name, e.target.value)}
                />
              )}
            </div>
          );
        })}

      <button className={styles.btn_primary} onClick={handleSubmit} type="button">
        {isEdit ? "Сохранить" : "Создать"}
      </button>
    </div>
  );
};
